import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export type TodoPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TodoStatus = 'pending' | 'finished' | 'completed';

export interface Todo {
    id: string;
    text: string;
    done: boolean; // kept for backward compat, derived from status
    status: TodoStatus;
    priority: TodoPriority;
    creatorId: 'me' | 'manager' | string;
    assignedToId?: string;
    conversationId?: string;
    dueDate?: string; // ISO date
    createdAt: number;
    completedAt?: number;
    finishedAt?: number;
    category?: string;
}

const TODOS_KEY = 'pos_todos';

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    private http = inject(HttpClient);
    private platformId = inject(PLATFORM_ID);

    readonly todos = signal<Todo[]>([]);
    readonly isLoading = signal(false);

    readonly pendingCount = computed(() => this.todos().filter(t => t.status === 'pending').length);
    readonly finishedCount = computed(() => this.todos().filter(t => t.status === 'finished').length);
    readonly completedCount = computed(() => this.todos().filter(t => t.status === 'completed').length);

    readonly overdueTodos = computed(() => {
        const now = Date.now();
        return this.todos().filter(t => t.status === 'pending' && t.dueDate && new Date(t.dueDate).getTime() < now);
    });

    readonly highPriorityTodos = computed(() =>
        this.todos().filter(t => t.status === 'pending' && (t.priority === 'high' || t.priority === 'urgent'))
    );

    private get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    /** Initialize: load from cache + fetch from API */
    async initialize(): Promise<void> {
        this.loadFromCache();
        this.isLoading.set(true);

        try {
            // TODO: Fetch from backend when API is available
            // const todos = await firstValueFrom(this.http.get<Todo[]>(`${environment.apiUrls.users}/tasks`));
            // this.todos.set(todos);
        } catch (err: any) {
            console.warn('[TaskService] API not available:', err?.message);
        } finally {
            this.isLoading.set(false);
        }
    }

    addTodo(text: string, creatorId: string = 'me', assignedToId?: string, priority: TodoPriority = 'medium', dueDate?: string, conversationId?: string) {
        const newTodo: Todo = {
            id: this.generateId(),
            text,
            done: false,
            status: 'pending',
            priority,
            creatorId,
            assignedToId,
            conversationId,
            dueDate,
            createdAt: Date.now()
        };
        this.todos.update(current => [newTodo, ...current]);
        this.saveToCache();
    }

    /** Receiver marks task as "terminé" (finished) */
    finishTodo(id: string) {
        this.todos.update(current =>
            current.map(todo => {
                if (todo.id === id && todo.status === 'pending') {
                    return { ...todo, status: 'finished' as TodoStatus, done: false, finishedAt: Date.now() };
                }
                return todo;
            })
        );
        this.saveToCache();
    }

    /** Sender marks task as "complété" (completed/approved) */
    completeTodo(id: string) {
        this.todos.update(current =>
            current.map(todo => {
                if (todo.id === id && (todo.status === 'finished' || todo.status === 'pending')) {
                    return { ...todo, status: 'completed' as TodoStatus, done: true, completedAt: Date.now() };
                }
                return todo;
            })
        );
        this.saveToCache();
    }

    /** Legacy toggle — for self-created tasks with no two-stage flow */
    toggleTodo(id: string) {
        const todo = this.todos().find(t => t.id === id);
        if (!todo) return;
        // Self-created tasks: simple toggle
        if (todo.creatorId === 'me' && !todo.assignedToId) {
            const newDone = todo.status !== 'completed';
            this.todos.update(current =>
                current.map(t => t.id === id ? {
                    ...t,
                    status: (newDone ? 'completed' : 'pending') as TodoStatus,
                    done: newDone,
                    completedAt: newDone ? Date.now() : undefined
                } : t)
            );
            this.saveToCache();
        }
    }

    /** Get a todo by id */
    getTodo(id: string): Todo | undefined {
        return this.todos().find(t => t.id === id);
    }

    updateTodo(id: string, updates: Partial<Todo>) {
        this.todos.update(current =>
            current.map(todo => todo.id === id ? { ...todo, ...updates } : todo)
        );
        this.saveToCache();
    }

    deleteTodo(id: string) {
        this.todos.update(current => current.filter(todo => todo.id !== id));
        this.saveToCache();
    }

    /** Clear all completed tasks */
    clearCompleted() {
        this.todos.update(current => current.filter(todo => todo.status !== 'completed'));
        this.saveToCache();
    }

    // ─── Helpers ───

    private generateId(): string {
        return `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    }

    // ─── Persistence ───

    private loadFromCache(): void {
        if (!this.isBrowser) return;
        try {
            const data = localStorage.getItem(TODOS_KEY);
            if (data) {
                const todos: Todo[] = JSON.parse(data);
                // Migrate old format: add status field if missing
                const migrated = todos.map(t => ({
                    ...t,
                    status: t.status || (t.done ? 'completed' : 'pending') as TodoStatus
                }));
                this.todos.set(migrated);
            }
        } catch { /* ignore */ }
    }

    private saveToCache(): void {
        if (!this.isBrowser) return;
        try {
            localStorage.setItem(TODOS_KEY, JSON.stringify(this.todos()));
        } catch { /* storage full */ }
    }
}
